import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useLocation } from "react-router-dom";
import { userSlice } from "../redux/user/userSlice";
import { getGamesData } from "../redux/asyncThunk/userActions";
import {
  CONTENT_TYPES,
  resolveGameContentType,
} from "../utils/contentType";

const { selectProject, showDialog, resetSelectedProject, closeDialog, exitHandle } =
  userSlice;

const EXPANDABLE_SECTIONS = [
  {
    type: CONTENT_TYPES.scenario,
    label: "Сценарии",
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
    empty: "Пока нет сценариев",
  },
  {
    type: CONTENT_TYPES.trainer,
    label: "Тренажёр",
    icon: <QuizOutlinedIcon fontSize="small" />,
    empty: "Пока нет тренажёров",
  },
  {
    type: CONTENT_TYPES.course,
    label: "Курсы",
    icon: <SchoolOutlinedIcon fontSize="small" />,
    empty: "Пока нет курсов",
  },
];

export default function SideMenu({ mobileOpen = false, onNavigate }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const games = useSelector((state) => state.user?.games);
  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const user = useSelector((state) => state.user?.data?.user);
  const [expandedTypes, setExpandedTypes] = useState({});

  const isRatingActive =
    location.pathname.startsWith("/main/rating") ||
    location.pathname.startsWith("/main/motivation");

  useEffect(() => {
    if (user?.id) {
      dispatch(getGamesData({ id: user.id }));
    }
  }, [dispatch, user?.id]);

  const groupedProjects = useMemo(() => {
    const groups = {
      [CONTENT_TYPES.knowledge_base]: [],
      [CONTENT_TYPES.scenario]: [],
      [CONTENT_TYPES.trainer]: [],
      [CONTENT_TYPES.course]: [],
    };

    for (const game of games ?? []) {
      const type = resolveGameContentType(game);
      groups[type]?.push(game);
    }

    return groups;
  }, [games]);

  useEffect(() => {
    if (!selectedGameId || !games?.length) return;

    const activeGame = games.find((game) => game._id === selectedGameId);
    if (activeGame) {
      const type = resolveGameContentType(activeGame);
      if (type !== CONTENT_TYPES.knowledge_base) {
        setExpandedTypes((current) => ({ ...current, [type]: true }));
      }
    }
  }, [selectedGameId, games]);

  const handleActive = (id) => {
    dispatch(selectProject(id));
  };

  const handleSectionToggle = (type) => {
    setExpandedTypes((current) => ({
      ...current,
      [type]: !current[type],
    }));
  };

  const handleNavigate = () => {
    onNavigate?.();
  };

  return (
    <aside
      className={`app-sidebar${
        mobileOpen ? " app-sidebar--mobile-open" : ""
      }`}
    >
      <div className="app-sidebar__top">
        <Button
          variant="contained"
          size="small"
          className="app-btn app-btn--primary"
          onClick={() => dispatch(showDialog("new"))}
        >
          Создать
        </Button>

        <Link to="/main" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary"
            onClick={() => {
              dispatch(resetSelectedProject());
              handleNavigate();
            }}
          >
            Рабочий стол
          </Button>
        </Link>

        <div className="app-sidebar__sections">
          <NavLink
            to="/main/knowledge-base"
            className={({ isActive }) =>
              `app-sidebar__direct-link${
                isActive ? " app-sidebar__direct-link--active" : ""
              }`
            }
            onClick={() => {
              dispatch(closeDialog());
              handleNavigate();
            }}
          >
            <span className="app-sidebar__direct-link-icon">
              <MenuBookOutlinedIcon fontSize="small" />
            </span>
            <span className="app-sidebar__direct-link-label">База знаний</span>
          </NavLink>

          <NavLink
            to="/main/rating"
            className={`app-sidebar__direct-link${
              isRatingActive ? " app-sidebar__direct-link--active" : ""
            }`}
            onClick={() => {
              dispatch(closeDialog());
              dispatch(resetSelectedProject());
              handleNavigate();
            }}
          >
            <span className="app-sidebar__direct-link-icon">
              <EmojiEventsOutlinedIcon fontSize="small" />
            </span>
            <span className="app-sidebar__direct-link-label">Рейтинг</span>
          </NavLink>

          {EXPANDABLE_SECTIONS.map(({ type, label, icon, empty }) => {
            const projects = groupedProjects[type] ?? [];
            const isExpanded = Boolean(expandedTypes[type]);

            return (
              <div
                key={type}
                className={`app-sidebar__section${
                  isExpanded ? " app-sidebar__section--expanded" : ""
                }`}
              >
                <button
                  type="button"
                  className="app-sidebar__section-toggle"
                  onClick={() => handleSectionToggle(type)}
                  aria-expanded={isExpanded}
                >
                  <span className="app-sidebar__section-toggle-main">
                    <span className="app-sidebar__section-icon">{icon}</span>
                    <span className="app-sidebar__section-label">{label}</span>
                  </span>
                  <span className="app-sidebar__section-toggle-meta">
                    <span className="app-sidebar__section-count">
                      {projects.length}
                    </span>
                    <ExpandMoreIcon
                      className="app-sidebar__section-chevron"
                      fontSize="small"
                    />
                  </span>
                </button>

                {isExpanded ? (
                  <div className="app-sidebar__section-body">
                    {!projects.length ? (
                      <div className="app-sidebar__empty">{empty}</div>
                    ) : (
                      <ul className="app-sidebar__list">
                        {projects.map((project) => (
                          <li key={project._id}>
                            <Link
                              to={`/main/${project._id}`}
                              state={{ projectId: project._id, type: "edit" }}
                              className={`app-sidebar__link${
                                selectedGameId === project._id &&
                                !location.pathname.startsWith("/main/knowledge-base") &&
                                !location.pathname.startsWith("/main/rating") &&
                                !location.pathname.startsWith("/main/motivation")
                                  ? " app-sidebar__link--active"
                                  : ""
                              }`}
                              onClick={() => {
                                handleActive(project._id);
                                dispatch(closeDialog());
                                handleNavigate();
                              }}
                            >
                              {project.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="app-sidebar__divider" />
      </div>

      <div className="app-sidebar__footer">
        <Link
          to="/main/profile"
          onClick={() => {
            dispatch(resetSelectedProject());
            handleNavigate();
          }}
          style={{ textDecoration: "none" }}
        >
          <Button
            variant="contained"
            size="small"
            className="app-btn app-btn--ghost app-sidebar__profile-btn"
            startIcon={<PersonOutlineIcon fontSize="small" />}
          >
            Профиль
          </Button>
        </Link>
        <Link
          to="/main/instruction"
          onClick={() => {
            dispatch(resetSelectedProject());
            handleNavigate();
          }}
          style={{ textDecoration: "none" }}
        >
          <Button variant="contained" size="small" className="app-btn app-btn--ghost">
            Обучение
          </Button>
        </Link>
        <Link
          to="/main/rules"
          onClick={() => {
            dispatch(resetSelectedProject());
            handleNavigate();
          }}
          style={{ textDecoration: "none" }}
        >
          <Button variant="contained" size="small" className="app-btn app-btn--ghost">
            Правила
          </Button>
        </Link>
        <Button
          variant="contained"
          size="small"
          className="app-btn app-btn--ghost app-sidebar__logout-btn"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={() => {
            handleNavigate();
            dispatch(exitHandle());
          }}
        >
          Выход
        </Button>
      </div>
    </aside>
  );
}
